import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Per-route head metadata for the main CRIAAA site.
 * Same non-destructive upsert pattern used by TenantHead: only touches
 * the specific tags it manages (title, description, canonical, og/twitter).
 */
function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href: string) {
  const links = document.head.querySelectorAll<HTMLLinkElement>('link[rel="canonical"]');
  // Keep exactly one canonical tag in the document
  links.forEach((el, i) => { if (i > 0) el.parentNode?.removeChild(el); });
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.appendChild(link);
  }
  link.href = href;
}

interface PageMetaProps {
  title: string;
  description: string;
}

export function PageMeta({ title, description }: PageMetaProps) {
  const location = useLocation();

  useEffect(() => {
    const url = `${window.location.origin}${location.pathname}`;

    document.title = title;
    upsertMeta('name', 'description', description);
    upsertCanonical(url);

    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', url);
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
  }, [title, description, location.pathname]);

  return null;
}
